import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';

const QRScanner = ({ onClose, onScan }) => {
  const { theme } = useTheme();
  const [isScanning, setIsScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup camera stream when component unmounts
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      setError(null);
      setIsScanning(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera if available
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions.');
      setIsScanning(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        // For demo purposes, simulate QR code detection
        // In a real app, you'd use a QR code detection library
        const mockQRData = {
          customerId: 'CUST_' + Math.random().toString(36).substr(2, 9),
          amount: Math.floor(Math.random() * 1000) + 100,
          timestamp: new Date().toISOString()
        };
        
        setScannedData(mockQRData);
        toast.success('QR Code detected from image!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScanResult = () => {
    if (scannedData) {
      onScan(scannedData);
      onClose();
    }
  };

  // Simulate QR detection for demo (in real app, use proper QR detection)
  const simulateQRDetection = () => {
    const mockQRData = {
      customerId: 'CUST_' + Math.random().toString(36).substr(2, 9),
      amount: Math.floor(Math.random() * 1000) + 100,
      timestamp: new Date().toISOString(),
      customerName: 'John Doe',
      phone: '+91' + Math.floor(Math.random() * 9000000000) + 1000000000
    };
    
    setScannedData(mockQRData);
    stopCamera();
    toast.success('QR Code detected!');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">QR Code Scanner</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {!scannedData ? (
            <div className="space-y-4">
              {/* Camera View */}
              {isScanning ? (
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-64 bg-black rounded-lg object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 border-2 border-white border-dashed rounded-lg"></div>
                  </div>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                    <button
                      onClick={simulateQRDetection}
                      className={`${theme.button} px-4 py-2 rounded-lg mr-2`}
                    >
                      Simulate Scan
                    </button>
                    <button
                      onClick={stopCamera}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Stop Camera
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    Scan customer QR codes to process transactions
                  </p>
                  <button
                    onClick={startCamera}
                    className={`${theme.button} px-6 py-3 rounded-lg mb-4 w-full`}
                  >
                    <Camera className="w-5 h-5 mr-2 inline" />
                    Start Camera
                  </button>
                </div>
              )}

              {error && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* File Upload Option */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2">Or upload QR code image:</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center space-x-2 border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors"
                >
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600">Upload QR Code Image</span>
                </button>
              </div>
            </div>
          ) : (
            /* Scanned Result */
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              <h4 className="text-lg font-semibold text-gray-900 mb-4">QR Code Scanned!</h4>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer ID:</span>
                    <span className="font-medium">{scannedData.customerId}</span>
                  </div>
                  {scannedData.customerName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">{scannedData.customerName}</span>
                    </div>
                  )}
                  {scannedData.phone && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium">{scannedData.phone}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">₹{scannedData.amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span className="font-medium">
                      {new Date(scannedData.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setScannedData(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Scan Again
                </button>
                <button
                  onClick={handleScanResult}
                  className={`flex-1 ${theme.button} py-2 px-4 rounded-lg transition-colors`}
                >
                  Process Transaction
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
